import React, { createContext, useContext, useEffect, useState } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Bump this key whenever we want to reset everyone to the new default.
// Old 'theme' key (light/dark) is intentionally ignored.
const THEME_KEY = 'theme_v2';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(THEME_KEY) as ThemeMode;
    if (saved === 'light' || saved === 'dark') return saved;
    return 'dark'; // default for all users on first load with new key
  });

  const toggleTheme = () => {
    setMode(prev => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem(THEME_KEY, newMode);
      return newMode;
    });
  };

  useEffect(() => {
    // Apply theme to document. Setting data-theme-switching for ~250ms enables
    // the scoped color-transition rule in globals.css so the flip animates,
    // without paying the cost on every hover.
    const root = document.documentElement;
    root.dataset.themeSwitching = '1';
    root.dataset.theme = mode;
    document.body.style.colorScheme = mode;
    const t = window.setTimeout(() => { delete root.dataset.themeSwitching; }, 250);
    return () => window.clearTimeout(t);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Current colour scheme, safe to call outside the provider.
 *
 * The MUI theme carries both schemes at once, so `theme.palette.mode` is pinned
 * to `defaultColorScheme` and no longer tracks the toggle — components branching
 * on the scheme read it from here instead. Unlike `useTheme` this does not throw
 * when there is no provider (an isolated render, e.g. a unit test): it falls back
 * to the attribute the provider stamps on <html>, then to MUI's default scheme.
 */
export function useThemeMode(): ThemeMode {
  const context = useContext(ThemeContext);
  if (context) return context.mode;
  const attr = typeof document !== 'undefined'
    ? document.documentElement.dataset.theme
    : undefined;
  return attr === 'dark' || attr === 'light' ? attr : 'light';
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
